import { getDisplayExchangeRate, isDefined } from '@eih-netbroker/common';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { useFormatter } from '../../../../../shared/services/utils/useFormatter';
import { ValidatedOrder } from '../../../services/order/useNormalOrderView';
import { CapturedExchangeData } from '../../../types/orderTypes';
import { OrderConfirmBlock, OrderConfirmRow } from './OrderConfirmComponents';
import classes from './OrderConfirmExchangeBlock.module.scss';

type OrderConfirmExchangeBlockProps = {
  orderForm: ValidatedOrder; // TODO: why not from common
  capturedExchangeData?: CapturedExchangeData;
};

export const OrderConfirmExchangeBlock = ({ orderForm, capturedExchangeData }: OrderConfirmExchangeBlockProps) => {
  const { translation } = useLocale();
  const { formatCurrencyMaxF5, formatCurrencyMaxF2 } = useFormatter();
  const t = translation.newOrderConfirm;

  const exchangeRateLabel = useMemo<string | undefined>(() => {
    if (!isDefined(capturedExchangeData?.summary?.exchangeRate)) {
      return undefined;
    }
    const { dominantCurrency, exchangeRate, secondaryCurrency } = getDisplayExchangeRate(
      capturedExchangeData.summary.exchangeRate,
    );
    return `1 ${dominantCurrency} = ${formatCurrencyMaxF5(exchangeRate, secondaryCurrency)}`;
  }, [capturedExchangeData?.summary?.exchangeRate, formatCurrencyMaxF5]);

  if (!isDefined(capturedExchangeData?.summary)) {
    return null;
  }

  return (
    <OrderConfirmBlock>
      {capturedExchangeData.summary.costInTargetCurrency > 0 && (
        <OrderConfirmRow
          label={t.freeCashCoverage}
          value={formatCurrencyMaxF5(capturedExchangeData.summary.costInTargetCurrency, orderForm.instrument.currency)}
          testIdBase="order-confirm-free-cash-coverage"
        />
      )}
      <OrderConfirmRow
        label={t.currencyCoverage}
        value={formatCurrencyMaxF5(
          capturedExchangeData.summary.costInSourceCurrency,
          capturedExchangeData.summary.sourceCurrency,
        )}
        testIdBase="order-confirm-currency-coverage"
      />

      <div className={classes.divider} />

      <OrderConfirmRow
        label={t.exchangeRate}
        value={exchangeRateLabel}
        testIdBase="order-confirm-exchange-rate"
      />
      {capturedExchangeData.summary.costInTargetCurrency > 0 && (
        <OrderConfirmRow
          label={t.exchangeTotalValue}
          value={formatCurrencyMaxF2(
            capturedExchangeData.summary.costInSourceCurrency *
              (1 / capturedExchangeData.summary.exchangeRate.exchangeRate),
            orderForm.instrument.currency,
          )}
          testIdBase="order-confirm-exchange-total-value"
        />
      )}
      <OrderConfirmRow
        label={t.settlementDate}
        value={format(capturedExchangeData.summary.settlementDate, 'yyyy. MM. dd.')}
        testIdBase="order-confirm-settlement-date"
      />
    </OrderConfirmBlock>
  );
};
